import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../utils/store";
import {
  listModels,
  OpenRouterModel,
  ModelListParams,
} from "../../api/openrouterApi";
import { ModelFilters } from "./ModelFilters";
import { ModelList } from "./ModelList";

export const ModelBrowser: React.FC = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const [search, setSearch] = useState("");
  const [providers, setProviders] = useState<string[]>([]);
  const [supportedParameters, setSupportedParameters] = useState<string[]>([]);
  const [data, setData] = useState<OpenRouterModel[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchModels = useCallback(async (): Promise<void> => {
    if (!token) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params: ModelListParams = {};

      if (search.trim()) {
        params.search = search.trim();
      }

      if (providers.length > 0) {
        params.providers = providers;
      }

      if (supportedParameters.length > 0) {
        params.supported_parameters = supportedParameters;
      }

      const result = await listModels(token, params);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load models");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token, search, providers, supportedParameters]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const handleSearchChange = (newSearch: string): void => {
    setSearch(newSearch);
  };

  const handleProvidersChange = (newProviders: string[]): void => {
    setProviders(newProviders);
  };

  const handleSupportedParametersChange = (newParameters: string[]): void => {
    setSupportedParameters(newParameters);
  };

  const handleClearFilters = (): void => {
    setSearch("");
    setProviders([]);
    setSupportedParameters([]);
  };

  return (
    <div className="tw-w-full">
      <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-6">
        Model Browser
      </h1>

      <ModelFilters
        search={search}
        providers={providers}
        supportedParameters={supportedParameters}
        onSearchChange={handleSearchChange}
        onProvidersChange={handleProvidersChange}
        onSupportedParametersChange={handleSupportedParametersChange}
        onClearFilters={handleClearFilters}
      />

      <ModelList
        data={data}
        loading={loading}
        error={error}
        token={token}
        onModelAdded={fetchModels}
      />
    </div>
  );
};
